import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';

// Auth Components
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import GoogleCallback from '@/pages/GoogleCallback';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';

// Legacy Pages (used in redirects and remaining functional routes)
import Intelligence from '@/pages/Intelligence';
import Settings from '@/pages/Settings';
import Billing from '@/pages/Billing';
import BillingSuccess from '@/pages/BillingSuccess';
import Creators from '@/pages/Creators';
import Admin from '@/pages/Admin';

// Admin Sub-pages (kept for direct access)
import ContentSources from '@/pages/admin/ContentSources';
import ProcessedAssets from '@/pages/admin/ProcessedAssets';
import SelfMarketing from '@/pages/admin/SelfMarketing';

// Legacy Routes (kept for remaining functional paths)
import Optimizer from '@/pages/Optimizer';
import Pricing from '@/pages/Pricing';
import ConnectedPlatforms from '@/pages/ConnectedPlatforms';
import AutopilotSettings from '@/pages/AutopilotSettings';

// BrandKit
import AdminBrandKit from '@/pages/AdminBrandKit';

// Public Pages
import LandingPage from '@/pages/LandingPage';
import PublicPricingPage from '@/pages/PublicPricingPage';

// Onboarding Pages (TEMP BYPASS: still imported to avoid breaking references)
import Welcome from '@/pages/onboarding/Welcome';
import ChoosePlan from '@/pages/onboarding/ChoosePlan';
import CreatorSetup from '@/pages/onboarding/CreatorSetup';
import StorageConnect from '@/pages/onboarding/StorageConnect';

// Admin Pricing
import AdminPricingSettings from '@/pages/AdminPricingSettings';

// New App Structure (CREATE/ANALYZE/ACCOUNT/ADMIN)
import NewDashboardLayout from '@/components/NewDashboardLayout';
import CreateHome from '@/pages/new-app/CreateHome';
import CreatorHub from '@/pages/new-app/CreatorHub';
import Creator from '@/pages/new-app/Creator';
import ContentStudio from '@/pages/new-app/ContentStudio';
import Workspace from '@/pages/new-app/Workspace';
import ProjectOverview from '@/pages/new-app/ProjectOverview';
import ProjectUpload from '@/pages/new-app/ProjectUpload';
import ImagesAndPosts from '@/pages/new-app/ImagesAndPosts';
import LivePage from '@/pages/new-app/LivePage';
import PublishPage from '@/pages/new-app/Publish';
import OptimizeWorkspace from '@/pages/new-app/OptimizeWorkspace';
import ScheduleWorkspace from '@/pages/new-app/ScheduleWorkspace';
import TemplatesWorkspace from '@/pages/new-app/TemplatesWorkspace';
import ClipsReview from '@/pages/new-app/ClipsReview';
import UnifiedEditor from '@/pages/new-app/UnifiedEditor';
import ClipsWorkspace from '@/pages/new-app/ClipsWorkspace';
import AutopilotCreate from '@/pages/new-app/AutopilotCreate';
import AnalyzeOverview from '@/pages/new-app/AnalyzeOverview';
import AnalyzeDashboard from '@/pages/new-app/AnalyzeDashboard';
import AnalyzeLive from '@/pages/new-app/AnalyzeLive';
import AnalyzeAgency from '@/pages/new-app/AnalyzeAgency';
import AnalyzeApp from '@/pages/new-app/AnalyzeApp';
import MonetizationInsights from '@/pages/new-app/MonetizationInsights';
import AccountOverview from '@/pages/new-app/AccountOverview';
import AccountHub from '@/pages/new-app/AccountHub';
import AccountSettings from '@/pages/new-app/AccountSettings';
import CreatorProfile from '@/pages/new-app/CreatorProfile';
import ProjectManager from '@/pages/new-app/ProjectManager';
import Affiliates from '@/pages/new-app/Affiliates';
import AffiliateDashboard from '@/pages/new-app/AffiliateDashboard';
import SupportPage from '@/pages/new-app/Support';
import AdminDashboard from '@/pages/new-app/AdminDashboard';
import AdminOverview from '@/pages/new-app/AdminOverview';
import AdminValuation from '@/pages/new-app/AdminValuation';
import AdminGrowth from '@/pages/new-app/AdminGrowth';
import AppSettings from '@/pages/new-app/AppSettings';
import AppProfiles from '@/pages/new-app/AppProfiles';
import AppProfile from '@/pages/new-app/AppProfile';
import TeamPermissions from '@/pages/new-app/TeamPermissions';
import ActivityLog from '@/pages/new-app/ActivityLog';
import EarnProMonth from '@/pages/new-app/EarnProMonth';
import Rewards from '@/pages/new-app/Rewards';
import AdminPayoutSettings from '@/pages/new-app/AdminPayoutSettings';
import AdminEarnProReview from '@/pages/new-app/AdminEarnProReview';
import AdminAffiliates from '@/pages/new-app/AdminAffiliates';
import AdminSupport from '@/pages/new-app/AdminSupport';
import SelfMarketingEngine from '@/pages/new-app/SelfMarketingEngine';
import SystemSettings from '@/pages/new-app/SystemSettings';
import AdminCommissions from '@/pages/new-app/AdminCommissions';
import ProductSkin from '@/pages/new-app/ProductSkin';

// Connect Section
import ConnectOverview from '@/pages/new-app/ConnectOverview';
import ConnectLeaderboards from '@/pages/new-app/ConnectLeaderboards';
import ConnectCommunity from '@/pages/new-app/ConnectCommunity';
import ConnectCreatorInbox from '@/pages/new-app/ConnectCreatorInbox';
import ConnectAudienceInbox from '@/pages/new-app/ConnectAudienceInbox';

// Account Placeholders
import ResourceToolkit from '@/pages/new-app/ResourceToolkit';
import ChurchPartner from '@/pages/new-app/ChurchPartner';
import UserFlywheel from '@/pages/new-app/UserFlywheel';
import UserTaskEngine from '@/pages/new-app/UserTaskEngine';

// Admin Placeholders
import InfrastructureMonitor from '@/pages/new-app/InfrastructureMonitor';
import ModerationSystem from '@/pages/new-app/ModerationSystem';
import FinancialControl from '@/pages/new-app/FinancialControl';
import FulfillmentSystem from '@/pages/new-app/FulfillmentSystem';
import SystemIntelligence from '@/pages/new-app/SystemIntelligence';
import AdminTaskEngine from '@/pages/new-app/AdminTaskEngine';
import PublicPagesSystem from '@/pages/new-app/PublicPagesSystem';
import CreatorSpotlightSystem from '@/pages/new-app/CreatorSpotlightSystem';
import AdminRewardSystem from '@/pages/new-app/AdminRewardSystem';

// Internal Dashboard
import InternalDashboard from '@/pages/InternalDashboard';

// Social Linking
import SocialAccounts from '@/pages/SocialAccounts';
import PostingSafety from '@/pages/PostingSafety';

// Support
import HelpCenter from '@/pages/HelpCenter';
import HelpArticle from '@/pages/HelpArticle';
import ContactSupport from '@/pages/ContactSupport';
import SupportTickets from '@/pages/SupportTickets';
import SupportTicketDetail from '@/pages/SupportTicketDetail';

// Landing & Pricing (already imported above)
import PublicPricing from '@/pages/PublicPricing';
import AdminLandingPage from '@/pages/AdminLandingPage';

// Storage
import StorageSettings from '@/pages/StorageSettings';

// Founder Manual
import FounderManual from '@/pages/FounderManual';
import FounderManualEditor from '@/pages/FounderManualEditor';
import AppBranding from '@/pages/AppBranding';

// Brands
import BrandList from '@/pages/BrandList';
import BrandCreate from '@/pages/BrandCreate';
import { BrandProvider } from '@/contexts/BrandContext';
import { AppProfileProvider } from '@/contexts/AppProfileContext';
import { ProductSkinProvider } from '@/contexts/ProductSkinContext';

// Legal
import LegalCenter from '@/pages/LegalCenter';
import LegalDocument from '@/pages/LegalDocument';
import LegalVault from '@/pages/LegalVault';
import LegalDocumentViewer from '@/pages/LegalDocumentViewer';
import LegalEditor from '@/pages/LegalEditor';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async (retryOnFail = false) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      });
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      // If this is a retry scenario (OAuth return), wait and try once more
      if (retryOnFail) {
        await new Promise(resolve => setTimeout(resolve, 500));
        try {
          const retryResponse = await axios.get(`${API}/auth/me`, {
            withCredentials: true,
            headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
          });
          setUser(retryResponse.data);
          setLoading(false);
          return;
        } catch {
          // Retry also failed
        }
      }
      setUser(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if returning from OAuth (has success or error query params)
    const params = new URLSearchParams(window.location.search);
    const isOAuthReturn = params.has('success') || params.has('error') || params.has('context');
    checkAuth(isOAuthReturn);
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#121417]">
        <div className="w-12 h-12 border-4 border-[#3A7BFF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, checkAuth, logout }}>
      <ProductSkinProvider>
        <BrandProvider>
          <AppProfileProvider>
            <BrowserRouter>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Signup only accessible after visiting pricing page (pricing-first gate) */}
              <Route
                path="/signup"
                element={!user ? <Signup /> : <Navigate to="/app/create" replace />}
              />
              <Route path="/auth/google/callback" element={<GoogleCallback />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help/article/:id" element={<HelpArticle />} />
              <Route path="/contact-support" element={<ContactSupport />} />

              {/* Legal - Public */}
              <Route path="/legal" element={<LegalCenter />} />
              <Route path="/legal/:slug" element={<LegalDocumentViewer />} />

              {/* =========================================================
                 TEMP: ONBOARDING BYPASS (Launch Unblocker)
                 - Redirect all onboarding paths to /app/create
                 - We will re-enable onboarding after Create is finalized
              ========================================================== */}
              <Route
                path="/onboarding/*"
                element={user ? <Navigate to="/app/create" replace /> : <Navigate to="/login" replace />}
              />

              {/* New App Structure - CREATE/ANALYZE/ACCOUNT/ADMIN */}
              <Route
                path="/app"
                element={user ? <NewDashboardLayout /> : <Navigate to="/login" replace />}
              >
                {/* CREATE */}
                <Route path="create" element={<CreatorHub />} />
                <Route path="create/hub" element={<CreatorHub />} />
                <Route path="create/creator" element={<Creator />} />
                <Route path="create/clips-review" element={<ClipsReview />} />
                <Route path="create/autopilot" element={<AutopilotCreate />} />
                <Route path="create/content-studio" element={<ContentStudio />} />
                <Route path="create/editor" element={<UnifiedEditor />} />
                <Route path="create/editor/:contentSourceId" element={<UnifiedEditor />} />
                <Route path="create/clips" element={<ClipsWorkspace />} />
                <Route path="create/clips/:contentSourceId" element={<ClipsWorkspace />} />
                <Route path="create/workspace" element={<Workspace />} />
                <Route path="create/workspace/project/:projectId" element={<ProjectOverview />} />
                <Route path="create/workspace/project/:projectId/upload" element={<ProjectUpload />} />
                <Route path="create/workspace/project/:projectId/live" element={<LivePage />} />
                <Route path="create/workspace/project/:projectId/images-posts" element={<ImagesAndPosts />} />
                <Route path="create/publish" element={<PublishPage />} />
                <Route path="create/optimize" element={<OptimizeWorkspace />} />
                <Route path="create/schedule" element={<ScheduleWorkspace />} />
                <Route path="create/templates" element={<TemplatesWorkspace />} />

                {/* ANALYZE */}
                <Route path="analyze" element={<AnalyzeOverview />} />
                <Route path="analyze/dashboard" element={<AnalyzeDashboard />} />
                <Route path="analyze/live" element={<AnalyzeLive />} />
                <Route path="analyze/app" element={<AnalyzeApp />} />
                <Route path="analyze/monetization-insights" element={<MonetizationInsights />} />
                {/* Legacy routes - redirect */}
                <Route path="analyze/agency" element={<Navigate to="/app/analyze/dashboard" replace />} />

                {/* CONNECT */}
                <Route path="connect" element={<ConnectOverview />} />
                <Route path="connect/leaderboards" element={<ConnectLeaderboards />} />
                <Route path="connect/community" element={<ConnectCommunity />} />
                <Route path="connect/creator-inbox" element={<ConnectCreatorInbox />} />
                <Route path="connect/audience-inbox" element={<ConnectAudienceInbox />} />

                {/* ACCOUNT */}
                <Route path="account" element={<AccountOverview />} />
                <Route path="account/settings" element={<AccountSettings />} />
                <Route path="account/creator-profile/:creatorId" element={<CreatorProfile />} />
                <Route path="account/creator-profile" element={<CreatorProfile />} />
                <Route path="account/team" element={<Navigate to="/app/account/project-manager?tab=team" replace />} />
                <Route path="account/billing" element={<Billing />} />
                <Route path="account/affiliates" element={<Affiliates />} />
                <Route path="account/affiliates/dashboard" element={<AffiliateDashboard />} />
                <Route path="account/earn-pro" element={<EarnProMonth />} />
                <Route path="account/rewards" element={<Rewards />} />
                <Route path="account/support" element={<SupportPage />} />
                <Route path="account/weekly-digest" element={<Navigate to="/app/account/project-manager?report=weekly" replace />} />
                <Route path="account/resource-toolkit" element={<ResourceToolkit />} />
                <Route path="account/church-partner" element={<ChurchPartner />} />
                {/* Project Manager — consolidates Project Management (Creator Growth Engine +
                    new Goals/Strategies/Projects/Tasks UI) and Team Management in one place */}
                <Route path="account/project-manager" element={<ProjectManager />} />
                {/* Legacy routes redirect into the consolidated Project Manager (pages preserved but hidden) */}
                <Route path="account/creator-growth-engine" element={<Navigate to="/app/account/project-manager?tab=project" replace />} />
                <Route path="account/user-flywheel" element={<Navigate to="/app/account/project-manager?tab=project" replace />} />
                <Route path="account/user-task-engine" element={<Navigate to="/app/account/project-manager?tab=project" replace />} />

                {/* ADMIN - Internal Admin Only */}
                <Route
                  path="admin"
                  element={user?.is_internal_admin ? <AdminDashboard /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/overview"
                  element={user?.is_internal_admin ? <AdminOverview /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/valuation"
                  element={user?.is_internal_admin ? <AdminValuation /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/growth"
                  element={user?.is_internal_admin ? <AdminGrowth /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/app-profiles"
                  element={user?.is_internal_admin ? <AppProfiles /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/app-profile/:profileId"
                  element={user?.is_internal_admin ? <AppProfile /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/app-profile"
                  element={user?.is_internal_admin ? <Navigate to="/app/admin/app-profiles" replace /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/app-settings"
                  element={user?.is_internal_admin ? <AppSettings /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/system-settings"
                  element={user?.is_internal_admin ? <SystemSettings /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/team-permissions"
                  element={user?.is_internal_admin ? <TeamPermissions /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/activity-log"
                  element={user?.is_internal_admin ? <ActivityLog /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/payout-settings"
                  element={user?.is_internal_admin ? <AdminPayoutSettings /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/commissions"
                  element={user?.is_internal_admin ? <AdminCommissions /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/product-skin"
                  element={user?.is_internal_admin ? <ProductSkin /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/affiliates"
                  element={user?.is_internal_admin ? <AdminAffiliates /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/self-marketing"
                  element={user?.is_internal_admin ? <SelfMarketingEngine /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/system-settings"
                  element={user?.is_internal_admin ? <SystemSettings /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/support"
                  element={user?.is_internal_admin ? <AdminSupport /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/infrastructure"
                  element={user?.is_internal_admin ? <InfrastructureMonitor /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/moderation"
                  element={user?.is_internal_admin ? <ModerationSystem /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/financial-control"
                  element={user?.is_internal_admin ? <FinancialControl /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/fulfillment"
                  element={user?.is_internal_admin ? <FulfillmentSystem /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/system-intelligence"
                  element={user?.is_internal_admin ? <SystemIntelligence /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/task-engine"
                  element={user?.is_internal_admin ? <AdminTaskEngine /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/public-pages"
                  element={user?.is_internal_admin ? <PublicPagesSystem /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/creator-spotlight"
                  element={user?.is_internal_admin ? <CreatorSpotlightSystem /> : <Navigate to="/app/create" replace />}
                />
                <Route
                  path="admin/reward-system"
                  element={user?.is_internal_admin ? <AdminRewardSystem /> : <Navigate to="/app/create" replace />}
                />
              </Route>

              {/* Old Routes - Use New Layout for Remaining Functional Routes */}
              <Route element={user ? <NewDashboardLayout /> : <Navigate to="/login" replace />}>
                {/* Redirects to New Structure */}
                <Route path="/dashboard" element={<Navigate to="/app/create" replace />} />
                <Route path="/upload" element={<Navigate to="/app/create/workspace" replace />} />
                <Route path="/live" element={<Navigate to="/app/create/workspace" replace />} />
                <Route path="/clips" element={<Navigate to="/app/create/workspace" replace />} />
                <Route path="/scheduler" element={<Navigate to="/app/create/schedule" replace />} />
                <Route path="/community" element={<Navigate to="/app/connect" replace />} />
                <Route path="/analytics" element={<Navigate to="/app/analyze/dashboard" replace />} />
                <Route path="/agency-portfolio" element={<Navigate to="/app/analyze/dashboard" replace />} />
                <Route path="/settings" element={<Navigate to="/app/account/settings" replace />} />
                <Route path="/billing" element={<Navigate to="/app/account/billing" replace />} />

                {/* Additional redirects for settings sub-routes */}
                <Route path="/settings/autopilot" element={<Navigate to="/app/account/settings" replace />} />
                <Route path="/platforms" element={<Navigate to="/app/account/settings" replace />} />

                {/* Keep these legacy routes functional for now */}
                <Route path="/optimizer" element={<Optimizer />} />
                <Route path="/intelligence" element={<Intelligence />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/creators" element={<Creators />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/content-sources" element={<ContentSources />} />
                <Route path="/admin/processed-assets" element={<ProcessedAssets />} />
                <Route path="/platforms" element={<ConnectedPlatforms />} />
                <Route path="/self-marketing" element={<SelfMarketing />} />
                <Route path="/internal-dashboard" element={<InternalDashboard />} />
                <Route path="/social-accounts" element={<SocialAccounts />} />
                <Route path="/settings/posting-safety" element={<PostingSafety />} />
                <Route path="/admin/landing-page" element={<AdminLandingPage />} />
                <Route path="/app-pricing" element={<Pricing />} />
                <Route path="/support/tickets" element={<SupportTickets />} />
                <Route path="/support/tickets/:id" element={<SupportTicketDetail />} />
                <Route path="/settings/storage" element={<StorageSettings />} />
                <Route path="/admin/founder-manual" element={<FounderManual />} />
                <Route path="/admin/founder-manual/:slug" element={<FounderManual />} />
                <Route path="/admin/founder-manual/edit/:slug" element={<FounderManualEditor />} />
                <Route path="/brands" element={<BrandList />} />
                <Route path="/brands/new" element={<BrandCreate />} />

                {/* Legal - Internal/Admin Only */}
                <Route path="/internal/legal" element={<LegalVault />} />
                <Route path="/internal/legal/editor" element={<LegalEditor />} />

                {/* App Branding - Admin Only */}
                <Route path="/admin/app-branding" element={<AppBranding />} />
                <Route path="/admin/brandkit" element={<AdminBrandKit />} />
                <Route path="/admin/pricing-settings" element={<AdminPricingSettings />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          </AppProfileProvider>
        </BrandProvider>
      </ProductSkinProvider>
    </AuthContext.Provider>
  );
}

export default App;
